// Hero section for the Summary view: "Is the plan working?".
// Layout: chart (full width) → deficit text → two cards side-by-side (Working TDEE + Suggested intake).

import React, { useState, useEffect, useMemo, useRef, memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import SvgIcon from "../../../../../parts/SvgIcon";
import Toast from "../../../../../parts/toast";
import { suggestedIntakeFor } from "../../../../../utils/nutrition-progress";
import { tdeeWarningState } from "./viewTokens";
import { SummaryHeroChartSwitcher } from "./SummaryHeroChartSwitcher";
import { TdeeAdjustModal } from "./TdeeAdjustModal";
import { TdeeCalculatorModal } from "./TdeeCalculatorModal";
import { TdeeExplainerModal } from "./TdeeExplainerModal";

/* ---- constants --------------------------------------------------------- */

const RATES = [
  { labelKey: "minus05", value: -0.5 },
  { labelKey: "minus025", value: -0.25 },
  { labelKey: "maintain", value: 0 },
  { labelKey: "plus025", value: 0.25 },
  { labelKey: "plus05", value: 0.5 },
];

const EDIT_GRADIENT_ICON = "/img/new-icons/edit-gradient-icon.svg";
const ARROW_RIGHT_ICON = "/img/new-icons/arrow-forward.svg";
const CALENDAR_ICON = "/img/new-icons/vuesax-linear-calendar.svg";

/* ---- i18n helper ------------------------------------------------------- */

const h = (t, key, opts) => t(`nutritionPage.progress.hero.${key}`, opts);

/* ---- deficit copy ------------------------------------------------------ */

const deficitCopy = (deficit, t) => {
  if (deficit > 400) return h(t, "copy.largeDeficit");
  if (deficit > 100) return h(t, "copy.moderateDeficit");
  if (deficit > 0)   return h(t, "copy.smallDeficit");
  if (deficit === 0) return h(t, "copy.maintenance");
  return h(t, "copy.surplus");
};

/* ---- need-more-data state ---------------------------------------------- */

const NeedMoreData = ({ expenditure, onAdjustClick }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div className="np-hero__card" style={{ gridColumn: "1 / -1" }}>
      <div className="np-hero__card-header">
        <div className="np-hero__card-label">{h(t, "workingTdee")}</div>
        {/* Feedback from product lead: a coach should be able to set a
            starting TDEE for a brand-new client even with zero logs/weigh-ins
            — this is the one entry point into the modal that doesn't require
            confident data (opens forced into "manual" mode, see canCalc in
            TdeeAdjustModal.js). */}
        <button
          type="button"
          data-button-type="secondary"
          className="np-hero__adjust-btn"
          onClick={onAdjustClick}
        >
          <SvgIcon src={EDIT_GRADIENT_ICON} classNames="np-hero__set-manually-icon" />
          <span className="np-plan-bar__cta-label">{h(t, "setTdeeManually")}</span>
        </button>
      </div>
      <p className="np-hero__card-sub np-hero__need-data-sub">
        {h(t, "needMoreData")}
      </p>
      <ul className="np-hero__need-data-list">
        {expenditure.totalDays < expenditure.needs.minDays && (
          <li>{h(t, "daysInRange", { total: expenditure.totalDays, min: expenditure.needs.minDays })}</li>
        )}
        {expenditure.loggedDays < expenditure.needs.minLogged && (
          <li>{h(t, "daysLogged", { logged: expenditure.loggedDays, min: expenditure.needs.minLogged })}</li>
        )}
        {expenditure.weighInCount < expenditure.needs.minWeighIns && (
          <li>{h(t, "weighIns", { count: expenditure.weighInCount, min: expenditure.needs.minWeighIns })}</li>
        )}
      </ul>
    </div>
  );
};

NeedMoreData.propTypes = {
  expenditure: PropTypes.object.isRequired,
  onAdjustClick: PropTypes.func.isRequired,
};

/* ---- TDEE card --------------------------------------------------------- */

const TdeeCard = ({ expenditure, onAdjustClick, onCalculateClick, onExplainClick }) => {
  const { t } = useTranslation("athlete-portal");
  const { isWarn, warningText } = tdeeWarningState(expenditure, t);

  return (
    <div className="np-hero__card">
      <div className="np-hero__card-header">
        <span className="np-hero__card-label">{h(t, "workingTdee")}</span>
        <div className="np-hero__card-actions">
          {/* Physiology-based reference (BMR/TDEE via Harris-Benedict), no
              persistence — a "Copy" button inside hands the value back to the
              coach, who pastes it into Adjust manually if they want it as the
              working TDEE. Deliberately not wired directly into that modal —
              see TdeeCalculatorModal.js's own header comment. */}
          <button
            type="button"
            data-button-type="secondary"
            className="np-hero__adjust-btn"
            onClick={onCalculateClick}
          >
            <SvgIcon src={CALENDAR_ICON} useCurrentColor classNames="np-plan-bar__cta-icon" />
            <span>{h(t, "calculate")}</span>
          </button>
          <button
            type="button"
            data-button-type="secondary"
            className="np-hero__adjust-btn"
            onClick={onAdjustClick}
          >
            <SvgIcon src={EDIT_GRADIENT_ICON} classNames="np-plan-bar__cta-icon" />
            <span className="np-plan-bar__cta-label">{h(t, "adjust")}</span>
          </button>
        </div>
      </div>
      <p className={`np-hero__card-value${isWarn ? " np-hero__card-value--warn" : ""}`}>
        ≈ {expenditure.workingExpenditure.toLocaleString()}
      </p>
      {/* estExpenditure only exists when confident (buildEnergyExpenditureStats
          leaves it undefined otherwise) — a manual override entered with no
          data yet (PRD feedback: coach sets a starting TDEE for a brand-new
          client) still renders workingExpenditure above, but there's nothing
          "calculated" to reference here. */}
      <p className="np-hero__card-sub np-hero__card-sub--spaced">
        {expenditure.confident
          ? h(t, "kcalDayCalculated", { value: expenditure.estExpenditure.toLocaleString() })
          : h(t, "manualNoCalc")}
      </p>
      {isWarn ? (
        <p className="np-hero__decoupled-warn">{warningText}</p>
      ) : (
        <p className="np-hero__card-sub">{h(t, "tdeeDesc")}</p>
      )}
      {/* Always available whenever this card renders (same as "Calculate" in
          the header above) — even a manually-set TDEE has an explanation,
          just not a back-calc one: the modal itself branches on `confident`
          to show either the real numbers or "why there's no number yet".
          Styled as the sibling card's "See full trends" text-button (same
          class) rather than a third solid button up in the header. */}
      <button type="button" className="np-hero__trends-link" onClick={onExplainClick}>
        {h(t, "explainerLink")}
        <SvgIcon src={ARROW_RIGHT_ICON} useCurrentColor classNames="np-plan-bar__chip-arrow" />
      </button>
    </div>
  );
};

TdeeCard.propTypes = {
  expenditure: PropTypes.object.isRequired,
  onAdjustClick: PropTypes.func.isRequired,
  onCalculateClick: PropTypes.func.isRequired,
  onExplainClick: PropTypes.func.isRequired,
};

/* ---- suggested intake card --------------------------------------------- */

const SuggestedIntakeCard = ({ expenditure, onNavigateToReports }) => {
  const { t } = useTranslation("athlete-portal");
  const [selectedRate, setSelectedRate] = useState(-0.5);
  const suggestedIntake = suggestedIntakeFor(expenditure.workingExpenditure, selectedRate);
  let rateSuffix = h(t, "suffixMaintenance");
  if (selectedRate < 0) rateSuffix = h(t, "suffixDeficit");
  else if (selectedRate > 0) rateSuffix = h(t, "suffixSurplus");

  return (
    <div className="np-hero__card">
      <div className="np-hero__card-header">
        <span className="np-hero__card-label">{h(t, "suggestedIntakeLabel")}</span>
      </div>
      <div className="np-hero__value-row">
        <div className="np-hero__intake-row">
          <span className="np-hero__card-value">{suggestedIntake.toLocaleString()}</span>
          <span className="np-hero__card-sub np-hero__card-sub--inline">{h(t, "kcalDaySuffix", { suffix: rateSuffix })}</span>
        </div>
        <div className="np-hero__rates">
          {RATES.map((r) => (
            <button
              key={r.value}
              type="button"
              data-button-type={selectedRate === r.value ? "primary" : "secondary"}
              onClick={() => setSelectedRate(r.value)}
            >
              {h(t, `rates.${r.labelKey}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="np-hero__card-sub np-hero__card-sub--spaced">
        {h(t, "carriesIntoPlan")}
      </p>
      <button type="button" className="np-hero__trends-link" onClick={onNavigateToReports}>
        {h(t, "seeTrends")}
        <SvgIcon src={ARROW_RIGHT_ICON} useCurrentColor classNames="np-plan-bar__chip-arrow" />
      </button>
    </div>
  );
};

SuggestedIntakeCard.propTypes = {
  expenditure: PropTypes.object.isRequired,
  onNavigateToReports: PropTypes.func,
};

/* ---- exported hero section --------------------------------------------- */

export const SummaryHero = memo(({
  series,
  macroSeries = [],
  phaseAreas,
  expenditure,
  units,
  athleteId,
  athlete,
  dailyStats = [],
  onTdeeOverrideSaved,
  onTdeeOverrideReset,
  onNavigateToReports,
}) => {
  const { t } = useTranslation("athlete-portal");

  // Modal open/close + save/reset orchestration lives here (TdeeCard's own
  // "Adjust" and NeedMoreData's "Set TDEE manually" both open the SAME
  // modal instance/state — no reason to duplicate it per entry point).
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  // TdeeCard's "Calculate" — separate modal/state, no persistence (see
  // TdeeCalculatorModal.js).
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  // TdeeCard's "How this is calculated" — read-only, nothing to save.
  const [isExplainerOpen, setIsExplainerOpen] = useState(false);

  // Scans the RAW dailyStats (not series/dayRows, which are filtered to the
  // selected reporting range) for the athlete's most recent logged weight —
  // a coach reviewing an old date range should still get the athlete's
  // actual current weight as the Calculator's prefill, not a stale one from
  // inside that range. Mirrors dayData.js's own bodyWeight coercion (arrives
  // as a numeric string sometimes) since this bypasses buildDayRows.
  const latestWeightKg = useMemo(() => {
    let latest = null;
    for (const stat of dailyStats) {
      const raw = stat?.bodyWeight;
      if (raw == null || !Number.isFinite(Number(raw))) continue;
      if (latest == null || stat.utcDate > latest.utcDate) {
        latest = { utcDate: stat.utcDate, weight: Number(raw) };
      }
    }
    return latest?.weight ?? null;
  }, [dailyStats]);

  // Client-side stand-in for the server-driven flash() Toast (page.js reads
  // locals.flash() at SSR render time, so it only works after a full page
  // reload) — this save is a fetch-without-reload, same situation as
  // TableTemplateControls.js, which sets this same precedent.
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);
  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type: "success", message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  // onTdeeOverrideSaved/onTdeeOverrideReset update tdeeOverrideState, which
  // lives up in NutritionProgress.js and is shared with ReportsView.js — a
  // successful save/reset here is what keeps Summary AND Reports in sync
  // (see NutritionProgress.js's own comment for the full "why").
  const handleSaveOverride = async ({ mode, underLogPct, manualValue }) => {
    const res = await fetch(`/actions/nutrition-tdee-override?proxyAthlete=${athleteId}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ mode, underLogPct, manualValue }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || data?.message || h(t, "adjustModal.errors.saveFailed"));
    }
    onTdeeOverrideSaved(data.override);
    showToast(h(t, "adjustModal.saveSuccess"));
  };

  const handleResetOverride = async () => {
    const res = await fetch(`/actions/nutrition-tdee-override/reset?proxyAthlete=${athleteId}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || data?.message || h(t, "adjustModal.errors.resetFailed"));
    }
    onTdeeOverrideReset();
    showToast(h(t, "adjustModal.resetSuccess"));
  };

  let deficitLabel = null;
  if (expenditure.confident) {
    deficitLabel = expenditure.deficit >= 0
      ? h(t, "deficitLabel", { value: expenditure.deficit.toLocaleString() })
      : h(t, "surplusLabel", { value: Math.abs(expenditure.deficit).toLocaleString() });
  }

  // A manual override with no data yet still yields a workingExpenditure
  // (buildEnergyExpenditureStats) — gate the cards on the VALUE existing,
  // not on `confident` (deficit/decoupled/weeklyChange stay confident-gated
  // below, they're genuinely undefined without real data).
  const hasWorkingValue = expenditure.workingExpenditure != null;

  return (
    <div className="np-hero">
      <SummaryHeroChartSwitcher
        series={series}
        macroSeries={macroSeries}
        phaseAreas={phaseAreas}
        expenditure={expenditure}
        units={units}
      />

      {/* Deficit summary */}
      {expenditure.confident && (
        <div className="np-hero__deficit">
          <p className="np-hero__deficit-label">{deficitLabel}</p>
          <p className="np-hero__deficit-sub">{deficitCopy(expenditure.deficit, t)}</p>
        </div>
      )}

      {/* TDEE + Suggested intake cards */}
      <div className="np-hero__cards">
        {hasWorkingValue && (
          <TdeeCard
            expenditure={expenditure}
            onAdjustClick={() => setIsAdjustOpen(true)}
            onCalculateClick={() => setIsCalculatorOpen(true)}
            onExplainClick={() => setIsExplainerOpen(true)}
          />
        )}
        {hasWorkingValue && (
          <SuggestedIntakeCard expenditure={expenditure} onNavigateToReports={onNavigateToReports} />
        )}
        {!hasWorkingValue && (
          <NeedMoreData expenditure={expenditure} onAdjustClick={() => setIsAdjustOpen(true)} />
        )}
      </div>

      <TdeeAdjustModal
        isOpen={isAdjustOpen}
        onClose={() => setIsAdjustOpen(false)}
        expenditure={expenditure}
        onSave={handleSaveOverride}
        onReset={handleResetOverride}
      />

      <TdeeExplainerModal
        isOpen={isExplainerOpen}
        onClose={() => setIsExplainerOpen(false)}
        expenditure={expenditure}
        units={units}
      />

      <TdeeCalculatorModal
        isOpen={isCalculatorOpen}
        onClose={() => setIsCalculatorOpen(false)}
        units={units}
        defaultGender={athlete?.gender}
        defaultWeightKg={latestWeightKg}
      />

      <Toast
        open={Boolean(toast)}
        type={toast?.type ?? "success"}
        message={toast?.message ?? ""}
      />
    </div>
  );
});

SummaryHero.propTypes = {
  series: PropTypes.array.isRequired,
  macroSeries: PropTypes.array,
  phaseAreas: PropTypes.array.isRequired,
  units: PropTypes.string,
  expenditure: PropTypes.object.isRequired,
  athleteId: PropTypes.string,
  athlete: PropTypes.object,
  dailyStats: PropTypes.array,
  onTdeeOverrideSaved: PropTypes.func,
  onTdeeOverrideReset: PropTypes.func,
  onNavigateToReports: PropTypes.func,
};
